/**
 * Formatacao leve do corpo das mensagens: **negrito**, *italico*, ~~riscado~~,
 * __sublinhado__, ||spoiler||, `codigo`, bloco de codigo com tres crases,
 * links http(s) e mencoes @handle.
 *
 * Sem estado nem acesso ao store — recebe o corpo e a lista de membros e
 * devolve nos de React. Nada de `dangerouslySetInnerHTML`: cada pedaco vira
 * elemento, entao texto do usuario nunca e interpretado como HTML. O unico
 * pedaco com estado proprio e o `Spoiler` (revela ao clicar), um componente
 * comum dentro da arvore que a funcao pura devolve.
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import { MENTION_EVERYONE } from "@shared/protocol";
import type { User } from "@shared/types";
import { C, FONT_MONO, hexA } from "@/theme";

const CODE_BLOCK: CSSProperties = {
  margin: "6px 0 2px",
  padding: "10px 12px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 8,
  background: C.field,
  color: C.textBody,
  fontFamily: FONT_MONO,
  fontSize: 12.5,
  lineHeight: 1.55,
  whiteSpace: "pre-wrap",
  overflowWrap: "break-word",
  overflowX: "auto",
};

const CODE_INLINE: CSSProperties = {
  padding: "1px 5px",
  borderRadius: 5,
  border: `1px solid ${C.lineStrong}`,
  background: C.field,
  color: C.textStrong,
  fontFamily: FONT_MONO,
  fontSize: 12.5,
};

const MENTION: CSSProperties = {
  padding: "0 4px",
  borderRadius: 5,
  background: "var(--acs, rgba(46,107,255,.14))",
  color: "var(--ac, #1F4FD8)",
  fontWeight: 600,
};

const LINK: CSSProperties = { color: C.accent, overflowWrap: "anywhere" };

const SPOILER_HIDDEN: CSSProperties = {
  padding: "0 4px",
  borderRadius: 4,
  background: C.textGhost,
  color: "transparent",
  cursor: "pointer",
  userSelect: "none",
};

const SPOILER_REVEALED: CSSProperties = {
  padding: "0 4px",
  borderRadius: 4,
  background: hexA(C.textGhost, 0.35),
  color: "inherit",
};

/** Texto oculto ate o clique, tipo `||spoiler||` do Discord. */
function Spoiler({ children }: { children: ReactNode }): ReactNode {
  const [revealed, setRevealed] = useState(false);
  return (
    <span
      onClick={() => setRevealed(true)}
      style={revealed ? SPOILER_REVEALED : SPOILER_HIDDEN}
      title={revealed ? undefined : "Clique para revelar"}
    >
      {children}
    </span>
  );
}

/** Bloco de codigo delimitado por tres crases. */
const FENCE_SOURCE = "```([\\s\\S]*?)```";

/**
 * Alternativas em ordem de prioridade. `**negrito**` vem antes de `*italico*`
 * para que o par duplo nunca seja lido como um par simples.
 */
const INLINE_SOURCE = [
  "(`[^`\\n]+`)",
  "(\\*\\*[^*\\n]+\\*\\*)",
  "(\\*[^\\s*][^*\\n]*\\*)",
  "(~~[^~\\n]+~~)",
  "(__[^_\\n]+__)",
  "(\\|\\|[^|\\n]+\\|\\|)",
  "(https?:\\/\\/[^\\s<>\"'`]+)",
  "@([a-z0-9._-]{3,24})",
].join("|");

/** Remove a etiqueta de linguagem e as quebras de sobra do bloco de codigo. */
function stripFence(raw: string): string {
  const text = raw.replace(/^\r?\n/, "");
  const brk = text.indexOf("\n");
  if (brk > -1) {
    const first = text.slice(0, brk).trim();
    if (first && /^[a-z0-9+#.-]{1,16}$/i.test(first)) {
      return text.slice(brk + 1).replace(/\s+$/, "");
    }
  }
  return text.replace(/\s+$/, "");
}

function countOf(text: string, char: string): number {
  let total = 0;
  for (const item of text) if (item === char) total += 1;
  return total;
}

/**
 * Separa a URL da pontuacao que veio grudada nela. "veja https://a.com."
 * nao deve virar um link terminado em ponto.
 */
function trimUrl(url: string): { href: string; rest: string } {
  let end = url.length;
  while (end > 0) {
    const char = url[end - 1];
    if (".,;:!?".includes(char)) {
      end -= 1;
      continue;
    }
    const head = url.slice(0, end);
    if (char === ")" && countOf(head, "(") < countOf(head, ")")) {
      end -= 1;
      continue;
    }
    break;
  }
  return { href: url.slice(0, end), rest: url.slice(end) };
}

/** Empurra texto puro preservando as quebras de linha. */
function pushText(nodes: ReactNode[], chunk: string, key: string): void {
  const lines = chunk.split("\n");
  lines.forEach((line, index) => {
    if (index > 0) nodes.push(<br key={`${key}-br${index}`} />);
    if (line) nodes.push(line);
  });
}

function renderInline(text: string, members: User[], keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const matcher = new RegExp(INLINE_SOURCE, "gi");
  let last = 0;
  let seq = 0;
  let match: RegExpExecArray | null;

  while ((match = matcher.exec(text)) !== null) {
    const whole = match[0];
    const code: string | undefined = match[1];
    const bold: string | undefined = match[2];
    const italic: string | undefined = match[3];
    const strike: string | undefined = match[4];
    const underline: string | undefined = match[5];
    const spoiler: string | undefined = match[6];
    const link: string | undefined = match[7];
    const handle: string | undefined = match[8];

    // "email@dominio" e "@@algo" nao sao mencoes.
    if (handle !== undefined && match.index > 0 && /[\w@]/.test(text[match.index - 1])) {
      continue;
    }

    if (match.index > last) {
      pushText(nodes, text.slice(last, match.index), `${keyPrefix}-p${seq}`);
      seq += 1;
    }
    const key = `${keyPrefix}-m${seq}`;
    seq += 1;
    last = match.index + whole.length;

    if (code !== undefined) {
      nodes.push(
        <code key={key} style={CODE_INLINE}>
          {code.slice(1, -1)}
        </code>,
      );
      continue;
    }
    if (bold !== undefined) {
      nodes.push(
        <strong key={key} style={{ fontWeight: 700, color: "#f0f0f0" }}>
          {renderInline(bold.slice(2, -2), members, `${key}-b`)}
        </strong>,
      );
      continue;
    }
    if (italic !== undefined) {
      nodes.push(
        <em key={key} style={{ fontStyle: "italic" }}>
          {renderInline(italic.slice(1, -1), members, `${key}-i`)}
        </em>,
      );
      continue;
    }
    if (strike !== undefined) {
      nodes.push(
        <s key={key} style={{ opacity: 0.75 }}>
          {renderInline(strike.slice(2, -2), members, `${key}-s`)}
        </s>,
      );
      continue;
    }
    if (underline !== undefined) {
      nodes.push(
        <span key={key} style={{ textDecoration: "underline" }}>
          {renderInline(underline.slice(2, -2), members, `${key}-u`)}
        </span>,
      );
      continue;
    }
    if (spoiler !== undefined) {
      nodes.push(
        <Spoiler key={key}>{renderInline(spoiler.slice(2, -2), members, `${key}-sp`)}</Spoiler>,
      );
      continue;
    }
    if (link !== undefined) {
      const { href, rest } = trimUrl(link);
      nodes.push(
        <a key={key} href={href} target="_blank" rel="noreferrer noopener" style={LINK}>
          {href}
        </a>,
      );
      if (rest) pushText(nodes, rest, `${key}-rest`);
      continue;
    }
    if (handle !== undefined) {
      const lower = handle.toLowerCase();
      const known =
        lower === MENTION_EVERYONE || members.some(user => user.handle.toLowerCase() === lower);
      if (known) {
        nodes.push(
          <span key={key} style={MENTION}>
            @{lower}
          </span>,
        );
      } else {
        pushText(nodes, whole, key);
      }
    }
  }

  if (last < text.length) pushText(nodes, text.slice(last), `${keyPrefix}-p${seq}`);
  return nodes;
}

/**
 * Converte o corpo da mensagem em nos de React.
 * `members` serve so para saber quais mencoes existem de verdade.
 */
export function renderRichText(body: string, members: User[], keyPrefix = "rt"): ReactNode[] {
  const nodes: ReactNode[] = [];
  const fence = new RegExp(FENCE_SOURCE, "g");
  let last = 0;
  let seq = 0;
  let match: RegExpExecArray | null;

  while ((match = fence.exec(body)) !== null) {
    if (match.index > last) {
      nodes.push(...renderInline(body.slice(last, match.index), members, `${keyPrefix}-t${seq}`));
    }
    nodes.push(
      <pre key={`${keyPrefix}-f${seq}`} style={CODE_BLOCK}>
        {stripFence(match[1])}
      </pre>,
    );
    last = match.index + match[0].length;
    seq += 1;
  }

  if (last < body.length) {
    nodes.push(...renderInline(body.slice(last), members, `${keyPrefix}-t${seq}`));
  }
  return nodes;
}

/**
 * Versao achatada do corpo, para citacoes de resposta e resultados de busca,
 * onde blocos e quebras nao cabem.
 */
export function plainPreview(body: string, max = 140): string {
  const flat = body
    .replace(/```[\s\S]*?```/g, " [código] ")
    .replace(/`([^`\n]+)`/g, "$1")
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/\*([^\s*][^*\n]*)\*/g, "$1")
    .replace(/~~([^~\n]+)~~/g, "$1")
    .replace(/__([^_\n]+)__/g, "$1")
    .replace(/\|\|([^|\n]+)\|\|/g, " [spoiler] ")
    .replace(/\s+/g, " ")
    .trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}
